import * as React from "react";
import {Button, Checkbox, Divider, Dropdown, DropdownItemProps, Input, Label, Popup, Segment} from "semantic-ui-react";
import {ConfigContext} from "../../../../context/ConfigContext";
import {notify} from 'react-notify-toast';

import 'brace/ext/searchbox'
import {backend} from "../../../../../../xconvert-backend";
import {authentication} from "../../../../../../authentication";
import {IfBox} from "../../../../../style/if";
import {
    AdditionalDataEntry,
    FreightStatus,
    QueuedEvent,
    ScriptErrorLogEntry,
    StopActionStatus,
    StopLocationStatus,
    TelematicState,
    TransportOrder,
    TransportOrderChange,
    TransportOrderStatus
} from "../../../../../../generated/index";
import {ConverterSendButton} from "../../ConverterSendButton";
import {EventEnum} from "../../../scriptExecution/scriptRunner/QueuedEventCreator";
import {TransportOrderSelector} from "../../../configChange/AiScripter/TransportOrderSelector";
import {TransportOrderJsonViewer} from "../../../configChange/AiScripter/TransportOrderJsonViewer";
import {FaListCheck} from "react-icons/fa6";
import {Signal, signal} from "@preact/signals-react";
import {exportSimErrors, exportSimInputIds, exportSimOutputIndex, exportSimOutputs} from "./ExportSimPrepareSide";
import {LuBrainCircuit} from "react-icons/lu";
import {TbReload} from "react-icons/tb";
import StatusCodeEnum = TransportOrderStatus.StatusCodeEnum;

export enum DropDownEnum {
    TRANSPORT_ORDER = "transportOrder",
    STOP_LOCATION = "stop location",
    STOP_ACTION = "stop action",
    FREIGHT = "freight",
}

export interface ExportSimTransportOrderEditorProps {
    sendEvent: (
        context: any,
        order: TransportOrder,
        orderId: string,
        selectedEvent: EventEnum,
        type: TransportOrderChange.TypeEnum,
        status: string | null,
        telematicStateCode: TelematicState.StatusCodeEnum,
        beforeSend?: (xReqId: string) => void,
        onSend?: (
            response: any,
            queuedEvent: QueuedEvent,
            respondedConfig: any,
            encoding: string,
            logEntry?: ScriptErrorLogEntry
        ) => void
    ) => Promise<string>

    beforeSend(xRequestId: string): void

    onSend(
        response: any,
        queuedEvent: QueuedEvent,
        respondedConfig: any,
        encoding: string,
        logEntry?: ScriptErrorLogEntry
    ): void
}

export const exportSimTransportOrders: Signal<TransportOrder[]> = signal([]);

export function ExportSimTransportOrderEditor(props: ExportSimTransportOrderEditorProps) {
    const context = React.useContext(ConfigContext);

    const [validJson, setValidJson] = React.useState<boolean>(true);
    const [isLoadingTransportOrder, setIsLoadingTransportOrder] = React.useState<boolean>(false);
    const [exportSimToId, setExportSimToId] = React.useState<string>(null);
    const [exportSimOrderId, setExportSimOrderId] = React.useState<string>(null);
    const [selectedEvent, setSelectedEvent] = React.useState<EventEnum>(null);
    const [type, setType] = React.useState<TransportOrderChange.TypeEnum>(null);
    const [telematicState, setTelematicState] = React.useState<boolean>(false);
    const [telematicStateCode, setTelematicStateCode] = React.useState<TelematicState.StatusCodeEnum>(null);
    const [status, setStatus] = React.useState<string>(null);
    const [showResolveStatusButton, setShowResolveStatusButton] = React.useState<boolean>(false);
    const [parsedTo, setParsedTo] = React.useState<TransportOrder | null>(null);
    const [showSelectorModal, setShowSelectorModal] = React.useState<boolean>(false);

    React.useEffect(() => {
        setSelectedEvent(EventEnum.ASSIGN_COMPANY_EVENT);
    }, []);

    function handleTransportOrderChange(value: string, index: number) {
        let valid: boolean;
        let parsedTo: TransportOrder = null;
        try {
            parsedTo = JSON.parse(value);
            exportSimTransportOrders.value[index] = parsedTo
            console.log(`added parsedTo to index ${index} -> `, exportSimTransportOrders.value)
            valid = true;
        } catch (e) {
            console.log("ERROR parsing order: ", value)
            valid = false;
        }

        if (parsedTo != null && parsedTo.status?.statusCode == TransportOrderStatus.StatusCodeEnum.INPUTMISSING) {
            setShowResolveStatusButton(true);
        }

        setValidJson(valid);

        setParsedTo(parsedTo);
        setIsLoadingTransportOrder(false);

    }

    async function handleLoadTransportOrder(orderId = exportSimToId, index: number = exportSimOutputIndex.value) {
        setIsLoadingTransportOrder(true);
        try {
            let auth = backend.withTokenAuthHeader(authentication.token);
            let transportOrder = await backend.transportOrderApi.fetchTransportOrderById(orderId, auth);

            let toAsJson = JSON.stringify(transportOrder, null, 4);
            handleTransportOrderChange(toAsJson, index);

        } finally {
            setIsLoadingTransportOrder(false);

        }
    }

    function _setExportSimOrderId(value) {
        context.setExportSimOrderCompanyId(value);
    }

    function canSendEvent() {
        return selectedEvent != null && (validJson && exportSimTransportOrders.value.length > 0);
    }

    function drawInput() {
        return <>
            <div style={{paddingRight: 20, display: "flex", gap: "10px"}}>
                <ConverterSendButton
                    disabled={!canSendEvent()}
                    onClick={async function (evt) {
                        console.log("Sending event ", selectedEvent, " with type ", type, " and status ", status);
                        exportSimOutputIndex.value = 0;
                        exportSimOutputs.value = [];
                        for (const order of exportSimTransportOrders.value) {
                            console.log("TO in exportSimTransportOrders: ", order?._id);

                            let errorMessage = await props.sendEvent(
                                context,
                                order,
                                order?._id,
                                selectedEvent,
                                type,
                                status,
                                telematicStateCode,
                                props.beforeSend,
                                props.onSend
                            );
                            console.log("Event sent");
                            exportSimErrors.value[exportSimOutputIndex.value] = errorMessage;
                            exportSimOutputIndex.value += 1;
                        }
                        exportSimOutputIndex.value = 0;
                    }}
                />
                <Popup
                    trigger={
                        <Button
                            icon
                            onClick={(evt) => {
                                exportSimTransportOrders.value = []
                                exportSimOutputIndex.value = 0
                                exportSimInputIds.value = []
                                exportSimOutputs.value = []
                                setShowSelectorModal(true)
                            }
                            }
                        >
                            <FaListCheck/>
                        </Button>
                    }
                    content={'Choose TransportOrders from list'}
                />

                <Popup
                    trigger={
                        <Input
                            style={{width: 150, marginRight: 36}}
                            placeholder='TransportOrderId'
                            value={exportSimToId}
                            onChange={(evt) => setExportSimToId(evt.target.value?.trim())}
                            action={
                                <Button
                                    icon
                                    onClick={(evt) => handleLoadTransportOrder()}
                                    loading={isLoadingTransportOrder}
                                >
                                    <TbReload/>
                                </Button>
                            }
                        />
                    }
                    content={"Load TransportOrder by Id"}
                    position={"bottom center"}
                />

                <Popup
                    trigger={
                        <Input
                            style={{width: 220}}
                            placeholder='OrderCompanyId'
                            value={exportSimOrderId}
                            onChange={(evt) => _setExportSimOrderId(evt.target.value?.trim())}
                        />
                    }
                    content={"CompanyId to override"}
                    position={"bottom center"}
                />
                <Popup
                    trigger={
                        <Button
                            icon
                            color="teal"
                            disabled={!exportSimToId || !context.scriptConverterPlaygroundScriptPath}
                            onClick={() => {
                                localStorage.setItem('aiScripterTransfer', JSON.stringify({
                                    transportOrderId: exportSimToId
                                }));
                                notify.show('Data sent to AI Scripter', 'success', 3000);
                                window.dispatchEvent(new CustomEvent('switchToAiScripter'));
                            }}
                        >
                            <LuBrainCircuit/>
                        </Button>
                    }
                    content={"AI-Scripter"}
                />
            </div>
            <Divider
                style={{marginTop: 2, marginBottom: 2}}
            />
            {drawTypeSelection()}
        </>
    }

    function resolveStatus() {

        let kdnr = {} as AdditionalDataEntry;
        kdnr.key = "kundenNr";
        kdnr.value = "7777777777777";

        parsedTo.transports.forEach(t => {
            t.stops.forEach(stop => {
                if (stop.additionalData != null) {
                    if (stop.additionalData.find(ad => ad.key == "kundenNr") == undefined) {
                        stop.additionalData.push(kdnr);
                    }
                } else {
                    stop.additionalData = [kdnr];
                }
            });
        });
        parsedTo.freight.forEach(f => {
            f.contractingParties.forEach(cp => {
                if (cp.additionalData != null) {
                    if (cp.additionalData.find(ad => ad.key == "kundenNr") == undefined) {
                        cp.additionalData.push(kdnr);
                    }
                } else {
                    cp.additionalData = [kdnr];
                }
            });
        });

        parsedTo.status.statusCode = StatusCodeEnum.ACCEPTED;

        handleTransportOrderChange(JSON.stringify(parsedTo, null, 4), exportSimOutputIndex.value);
        setShowResolveStatusButton(false)
    }

    function enumToArray() {
        let enumValues: Array<DropdownItemProps> = [];
        Object.values(EventEnum).forEach(value => {
            enumValues.push({key: value, value: value, text: value});
        });
        return enumValues;
    }

    function drawTypeSelection() {
        let options = enumToArray();
        return <div style={{display: "flex", flexDirection: "row", flexWrap: "wrap"}}>

            <Dropdown
                selectOnBlur={false}
                placeholder='Select Type'
                search
                selection
                options={options}
                clearable
                defaultValue={EventEnum.ASSIGN_COMPANY_EVENT}
                onChange={function (event, {value}) {
                    console.log(value);
                    setSelectedEvent(value as EventEnum);
                }}
            />
            <IfBox shouldShow={selectedEvent == EventEnum.STATUS_UPDATE_EVENT}>
                <div style={{paddingLeft: 20}}>
                    <p>Update
                        <Dropdown
                            selectOnBlur={false}
                            placeholder='Select'
                            search
                            style={{marginLeft: 10, marginRight: 10}}
                            selection
                            options={statusTypeEnumToArray()}
                            clearable
                            onChange={_setType}
                        />
                        Status</p>
                </div>

                <div style={{paddingLeft: 20, display: "flex", flexDirection: "column", flexWrap: "wrap"}}>

                    <p>To
                        <Dropdown
                            selectOnBlur={false}
                            disabled={type == null}
                            placeholder='Select Status'
                            search
                            style={{marginLeft: 10, marginRight: 10}}
                            selection
                            options={statusEnumToArray()}
                            clearable
                            onChange={_setStatus}
                            value={getStatus()}
                        />
                    </p>
                    <IfBox
                        shouldShow={type == TransportOrderChange.TypeEnum.STATUSUPDATETRANSPORTORDER}>
                        <div style={{paddingLeft: 25}}>
                            Status -
                            <Checkbox
                                disabled={type != TransportOrderChange.TypeEnum.STATUSUPDATETRANSPORTORDER}
                                type='checkbox'
                                toggle
                                onChange={() => {
                                    setTelematicState(!telematicState)
                                    setStatus(null)
                                    setTelematicStateCode(null)
                                }}
                            />
                            - TelematicState
                        </div>
                    </IfBox>

                </div>

            </IfBox>
        </div>
    }

    function statusTypeEnumToArray() {
        let enumValues: Array<DropdownItemProps> = [];
        Object.values(DropDownEnum).forEach(value => {
            enumValues.push({key: value, value: value, text: value});
        });
        return enumValues;
    }

    function statusEnumToArray() {
        let enumValues: Array<DropdownItemProps> = [];

        switch (type) {
            case TransportOrderChange.TypeEnum.STATUSUPDATETRANSPORTORDER: {
                if (telematicState) {
                    Object.values(TelematicState.StatusCodeEnum).forEach(value => {
                        enumValues.push({key: value, value: value, text: value});
                    });
                } else {
                    Object.values(TransportOrderStatus.StatusCodeEnum).forEach(value => {
                        enumValues.push({
                            key: value,
                            value: value,
                            text: value,
                        });
                    });
                }
                break;
            }
            case TransportOrderChange.TypeEnum.STATUSUPDATESTOPLOCATION: {
                Object.values(StopLocationStatus.StatusCodeEnum).forEach(value => {
                    enumValues.push({
                        key: value,
                        value: value,
                        text: value,
                    });
                });
                break;
            }
            case TransportOrderChange.TypeEnum.STATUSUPDATESTOPACTION: {
                Object.values(StopActionStatus.StatusCodeEnum).forEach(value => {
                    enumValues.push({
                        key: value,
                        value: value,
                        text: value,
                    });
                });
                break;
            }
            case TransportOrderChange.TypeEnum.STATUSUPDATEFREIGHT: {
                Object.values(FreightStatus.StatusCodeEnum).forEach(value => {
                    enumValues.push({
                        key: value,
                        value: value,
                        text: value,
                    });
                });
                break;
            }
        }
        return enumValues;
    }

    function _setType(event, {value}) {
        console.log(value);

        switch (event.target.textContent) {
            case DropDownEnum.TRANSPORT_ORDER:
                setType(TransportOrderChange.TypeEnum.STATUSUPDATETRANSPORTORDER)
                break;

            case DropDownEnum.STOP_LOCATION:
                setType(TransportOrderChange.TypeEnum.STATUSUPDATESTOPLOCATION);
                break;

            case DropDownEnum.STOP_ACTION:
                setType(TransportOrderChange.TypeEnum.STATUSUPDATESTOPACTION);
                break;

            case DropDownEnum.FREIGHT:
                setType(TransportOrderChange.TypeEnum.STATUSUPDATEFREIGHT);
                break;
        }
    }

    function _setStatus(event, {value}) {
        console.log(value);
        let status = event.target.textContent;
        console.log(status);
        if (telematicState) {
            setTelematicStateCode(status)
            setStatus(null);
        } else {
            setStatus(status);
            setTelematicStateCode(null);
        }
    }

    function getStatus() {
        if (telematicState) {
            return telematicStateCode;
        } else {
            return status;
        }
    }

    function transportOrderSelector() {
        function orderButton(order: TransportOrder, index: number) {
            return <Button
                size="mini"
                key={"orderButton_" + index}
                primary={index === exportSimOutputIndex.value}
                onClick={() => {
                    console.log("setting index to " + index);
                    exportSimOutputIndex.value = index
                }}
            >
                {order?._id}
            </Button>
        }

        if (exportSimTransportOrders.value.length <= 1) {
            return null;
        }
        return <span style={{marginBottom: "3px", marginTop: "3px"}}>
            {exportSimTransportOrders.value.map((order, index) => orderButton(order, index))}
        </span>;
    }

    return (
        <>
            {drawInput()}
            {!validJson ? <Label size={'medium'} color='red' pointing="below">INVALID JSON</Label> : null}
            {showResolveStatusButton ?
                <Segment textAlign={'center'}>
                    <Label color={'yellow'}>
                        The status is INPUT_MISSING. This won't work on the Converter.
                    </Label>
                    <br/>
                    <Button
                        onClick={() => resolveStatus()}
                    >
                        Resolve
                    </Button>
                </Segment> : null
            }
            {transportOrderSelector()}
            <TransportOrderJsonViewer
                transportOrder={JSON.stringify(exportSimTransportOrders.value[exportSimOutputIndex.value], null, 4)}
                readOnly={false}
                onChange={(value) => handleTransportOrderChange(value, exportSimOutputIndex.value)}
            />
            <TransportOrderSelector
                isOpen={showSelectorModal}
                onClose={() => {
                    setShowSelectorModal(false)
                    exportSimOutputIndex.value = 0;
                }}
                transportOrderIds={exportSimToId ? [exportSimToId] : []}
                setTransportOrderIds={(ids) => {
                    ids.forEach((id, index) => {
                        handleLoadTransportOrder(id, index).then();
                        exportSimOutputIndex.value = index;
                        console.log(`Added transport order with id ${id} and index: ${index}`);
                        console.log("exportSimTransportOrders", exportSimTransportOrders.value);
                    })
                }}
                allowMultipleSelection={true}
            />
        </>
    );
}